import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { Language, Translation, getTranslation } from "@/locales/translations";
import { useAuth } from "./useAuth";

import { logger } from '@/services/logger';
interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  t: Translation;
  availableLanguages: { code: Language; name: string; flag: string }[];
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const LANGUAGE_STORAGE_KEY = "knitido_user_language";

// Storage key for user-specific language preferences
const getUserLanguageKey = (userId: string) => `knitido_language_${userId}`;

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  
  // Initialize language from user preference or browser default
  const getInitialLanguage = (): Language => {
    try {
      // First check user-specific preference if logged in
      if (user?.id) {
        const userLanguage = localStorage.getItem(getUserLanguageKey(user.id)) as Language;
        if (userLanguage && ['de', 'en', 'ja', 'fr', 'es', 'it', 'nl', 'pl', 'lv', 'et', 'lt', 'fi', 'sv', 'no', 'da', 'sl', 'ro', 'hu', 'cs', 'sk', 'pt', 'el', 'bg', 'hr'].includes(userLanguage)) {
          return userLanguage;
        }
      }

      // Then check general saved preference
      const savedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY) as Language;
      if (savedLanguage && ['de', 'en', 'ja', 'fr', 'es', 'it', 'nl', 'pl', 'lv', 'et', 'lt', 'fi', 'sv', 'no', 'da', 'sl', 'ro', 'hu', 'cs', 'sk', 'pt', 'el', 'bg', 'hr'].includes(savedLanguage)) {
        return savedLanguage;
      }
      
      // Fallback to browser language
      const browserLanguage = navigator.language.toLowerCase();
      if (browserLanguage.startsWith('de')) return 'de';
      if (browserLanguage.startsWith('fr')) return 'fr';
      if (browserLanguage.startsWith('es')) return 'es';
      if (browserLanguage.startsWith('it')) return 'it';
      if (browserLanguage.startsWith('nl')) return 'nl';
      if (browserLanguage.startsWith('pl')) return 'pl';
      if (browserLanguage.startsWith('lv')) return 'lv';
      if (browserLanguage.startsWith('et')) return 'et';
      if (browserLanguage.startsWith('lt')) return 'lt';
      if (browserLanguage.startsWith('fi')) return 'fi';
      if (browserLanguage.startsWith('sv')) return 'sv';
      if (browserLanguage.startsWith('no') || browserLanguage.startsWith('nb') || browserLanguage.startsWith('nn')) return 'no';
      if (browserLanguage.startsWith('da')) return 'da';
      if (browserLanguage.startsWith('sl')) return 'sl';
      if (browserLanguage.startsWith('ro')) return 'ro';
      if (browserLanguage.startsWith('hu')) return 'hu';
      if (browserLanguage.startsWith('cs')) return 'cs';
      if (browserLanguage.startsWith('sk')) return 'sk';
      if (browserLanguage.startsWith('pt')) return 'pt';
      if (browserLanguage.startsWith('el')) return 'el';
      if (browserLanguage.startsWith('bg')) return 'bg';
      if (browserLanguage.startsWith('hr')) return 'hr';
      // For Japanese browser, use Japanese
      if (browserLanguage.startsWith('ja')) return 'ja';
      // All other languages default to English
      return 'en';
    } catch (error) {
      logger.error('Error getting initial language:', error);
      return 'en';
    }
  };

  const [language, setLanguageState] = useState<Language>(getInitialLanguage);

  // Update language when user changes
  useEffect(() => {
    if (user?.id) {
      const userLanguage = localStorage.getItem(getUserLanguageKey(user.id)) as Language;
      if (userLanguage && ['de', 'en', 'ja', 'fr', 'es', 'it', 'nl', 'pl', 'lv', 'et', 'lt', 'fi', 'sv', 'no', 'da', 'sl', 'ro', 'hu', 'cs', 'sk', 'pt', 'el', 'bg', 'hr'].includes(userLanguage)) {
        setLanguageState(userLanguage);
      }
    }
  }, [user?.id]);

  const setLanguage = (newLanguage: Language) => {
    setLanguageState(newLanguage);
    
    try {
      // Save to general localStorage
      localStorage.setItem(LANGUAGE_STORAGE_KEY, newLanguage);
      
      // Save user-specific preference if logged in
      if (user?.id) {
        localStorage.setItem(getUserLanguageKey(user.id), newLanguage);
      }
    } catch (error) {
      logger.error('Error saving language preference:', error);
    }
  };

  const availableLanguages = [
    { code: 'bg' as Language, name: 'Български', flag: '🇧🇬' },
    { code: 'cs' as Language, name: 'Čeština', flag: '🇨🇿' },
    { code: 'da' as Language, name: 'Dansk', flag: '🇩🇰' },
    { code: 'de' as Language, name: 'Deutsch', flag: '🇩🇪' },
    { code: 'el' as Language, name: 'Ελληνικά', flag: '🇬🇷' },
    { code: 'et' as Language, name: 'Eesti', flag: '🇪🇪' },
    { code: 'en' as Language, name: 'English', flag: '🇬🇧' },
    { code: 'es' as Language, name: 'Español', flag: '🇪🇸' },
    { code: 'fr' as Language, name: 'Français', flag: '🇫🇷' },
    { code: 'hr' as Language, name: 'Hrvatski', flag: '🇭🇷' },
    { code: 'it' as Language, name: 'Italiano', flag: '🇮🇹' },
    { code: 'lv' as Language, name: 'Latviešu', flag: '🇱🇻' },
    { code: 'lt' as Language, name: 'Lietuvių', flag: '🇱🇹' },
    { code: 'hu' as Language, name: 'Magyar', flag: '🇭🇺' },
    { code: 'nl' as Language, name: 'Nederlands', flag: '🇳🇱' },
    { code: 'no' as Language, name: 'Norsk', flag: '🇳🇴' },
    { code: 'pl' as Language, name: 'Polski', flag: '🇵🇱' },
    { code: 'pt' as Language, name: 'Português', flag: '🇵🇹' },
    { code: 'ro' as Language, name: 'Română', flag: '🇷🇴' },
    { code: 'sk' as Language, name: 'Slovenčina', flag: '🇸🇰' },
    { code: 'sl' as Language, name: 'Slovenščina', flag: '🇸🇮' },
    { code: 'fi' as Language, name: 'Suomi', flag: '🇫🇮' },
    { code: 'sv' as Language, name: 'Svenska', flag: '🇸🇪' },
    { code: 'ja' as Language, name: '日本語', flag: '🇯🇵' },
  ];

  const t = getTranslation(language);

  const value: LanguageContextType = {
    language,
    setLanguage,
    t,
    availableLanguages,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};